import searchAtomPair, { SearchParams } from 'atoms/search-atom';
import {
  Checkbox,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  SkeletonPlaceholder,
  Search,
  DefinitionTooltip,
  FlexGrid as Grid,
  Row,
  Tile,
  Table,
} from '@carbon/react';
import { Checkmark, CopyFile, Events } from '@carbon/react/icons';
import style from './entity-search.module.scss';
import { EntityDetailsPopover } from 'components/entity-details-popover';
import { Link } from 'components/link';
import { SkeletonTableRows } from 'components/skeleton-table-rows';
import { PaginatedTable } from 'components/table/paginated-table';
import { useAtom, useSetAtom } from 'jotai';
import { debounce } from 'lodash-es';
import { ReactNode, useCallback, useMemo, useState, useEffect } from 'react';
import { EmptyState } from 'components/empty-state';
import ErrorToast from 'components/error-toast';
import { EntityResponse } from 'types/api-responses';
import { format } from 'lib/ui';
import plur from 'plur';
import { useHistory, useLocation } from 'react-router-dom';

const DEFAULT_PAGE_SIZE = 16;

type EntitySearchParams = {
  hasInventory: boolean;
} & SearchParams;
const useSearchAtom = (href?: string) => {
  const location = useLocation();

  return useMemo(() => {
    const params = new URLSearchParams(location.search);
    const searchTerm = params.get('search') || '';
    const hasInventoryParam = params.get('hasInventory') === 'true';
    const page = parseInt(params.get('page') || '1', 10);

    return searchAtomPair<EntityResponse, EntitySearchParams>(
      href ? { href } : 'entities',
      {
        page: page,
        pageSize: DEFAULT_PAGE_SIZE,
        hasInventory: hasInventoryParam,
        text: searchTerm,
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [href]);
};

const EntitySearch = ({
  href,
  onEntityClick,
}: {
  href?: string;
  onEntityClick?: (entity: EntityResponse) => ReactNode;
}) => {
  const searchAtoms = useSearchAtom(href);

  return (
    <>
      <Grid className="bx--no-gutter" fullWidth>
        <Row
          className={
            onEntityClick ? style.modalEntityFilters : style.entityFilters
          }
        >
          <EntityTypeFilter searchAtoms={searchAtoms} />
        </Row>

        <EntitySearchTable
          searchAtoms={searchAtoms}
          onEntityClick={onEntityClick}
        />
      </Grid>
    </>
  );
};

const EntityTypeFilter = ({
  searchAtoms,
}: {
  searchAtoms: ReturnType<typeof useSearchAtom>;
}) => {
  const { searchAtom } = searchAtoms;
  const [search] = useAtom(searchAtom);

  const totalEntities = search.data ? (
    `${format.number(search.data.totalCount || 0)} ${plur(
      'entity',
      search.data.totalCount
    )}`
  ) : search.loading ? (
    <SkeletonPlaceholder className={style.placeholderSkeletonItem} />
  ) : null;
  return (
    <>
      <h2 className={style.totalEntities}>{totalEntities}</h2>
      <div className={style.actionsArea}>
        <EntitiesSearchField searchAtoms={searchAtoms} />
      </div>
    </>
  );
};

const EntitiesSearchField = ({
  searchAtoms: { searchAtom, resetSearchDataAtom },
}: {
  searchAtoms: ReturnType<typeof useSearchAtom>;
}) => {
  const location = useLocation();
  const history = useHistory();

  const [searchDisplayText, setSearchDisplayText] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('search') || '';
  });

  const [searchResults, setSearchPayload] = useAtom(searchAtom);
  const resetSearchData = useSetAtom(resetSearchDataAtom);

  // Debounced function to update search payload and URL
  const debouncedSetSearchPayload = useMemo(
    () =>
      debounce((value: string) => {
        resetSearchData(undefined);

        const params = new URLSearchParams(location.search);
        if (value) {
          params.set('search', value);
        } else {
          params.delete('search');
        }
        params.set('page', '1'); // Reset the 'page' query parameter to 1

        history.replace({ search: params.toString() });

        setSearchPayload((current) => ({
          ...current,
          pageSize: current?.pageSize || 10,
          text: value,
          page: 1,
          hasInventory: current?.hasInventory ?? false, // Ensure boolean
        }));
      }, 400),
    [location.search, history, resetSearchData, setSearchPayload]
  );

  // Function to handle the checkbox state and synchronize with the URL
  const setHasInventory = useCallback(
    (isChecked: boolean) => {
      const params = new URLSearchParams(location.search);

      if (isChecked) {
        params.set('hasInventory', 'true');
      } else {
        params.delete('hasInventory');
      }
      params.set('page', '1'); // Reset the 'page' query parameter to 1

      history.replace({ search: params.toString() });

      setSearchPayload((current) => ({
        ...current,
        hasInventory: isChecked, // Pass parameter value
        pageSize: current?.pageSize || 10,
        page: 1,
      }));
    },
    [location.search, history, setSearchPayload]
  );

  // Cleanup debounced function on component unmount
  useEffect(() => {
    return () => {
      debouncedSetSearchPayload.cancel();
    };
  }, [debouncedSetSearchPayload]);

  const searchParams = searchResults.currentPayload;

  return (
    <div className={style.entitiesSearchField}>
      {searchResults?.error ? (
        <ErrorToast message={searchResults?.error} />
      ) : null}
      <div className={style.hasInventoryCheckboxArea}>
        <Checkbox
          id="has-inventory"
          checked={searchParams?.hasInventory || false}
          onChange={(_evt, { checked }: { checked: boolean }) => {
            setHasInventory(checked);
          }}
          labelText="With Inventory Only"
        />
      </div>
      <Search
        labelText="Search"
        placeholder="Search"
        size="lg"
        {...{ autoFocus: true }}
        value={searchDisplayText}
        onChange={(e) => {
          setSearchDisplayText(e.target.value);
          debouncedSetSearchPayload(e.target.value);
        }}
      />
    </div>
  );
};

const EntitySearchTable = ({
  searchAtoms: { searchAtom },
  onEntityClick,
}: {
  searchAtoms: ReturnType<typeof useSearchAtom>;
  onEntityClick?: (entity: EntityResponse) => ReactNode;
}) => {
  const [searchResults, setSearchPayload] = useAtom(searchAtom);
  const searchParams = searchResults.currentPayload;
  const pageSize = searchResults?.data?.pageSize || DEFAULT_PAGE_SIZE;

  return (
    <PaginatedTable
      pageSizes={[DEFAULT_PAGE_SIZE, 24, 48, 64]}
      pageSize={pageSize}
      className={style.entitiesTable}
      page={searchParams?.page || 1}
      totalItems={searchResults?.data?.totalCount || 0}
      persistentPagination
      onPaginate={({ page, pageSize }) => {
        setSearchPayload(
          (current) =>
            current && {
              ...current,
              page,
              pageSize,
            }
        );
      }}
      isLoading={searchResults.loading || false}
      loadingComponent={
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader></TableHeader>
              <TableHeader>Id</TableHeader>
              <TableHeader>Name</TableHeader>
              <TableHeader>Addresses</TableHeader>
              <TableHeader>Phone Numbers</TableHeader>
              <TableHeader>Emails</TableHeader>
              <TableHeader>Entity Type</TableHeader>
              <TableHeader>Inventory</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            <SkeletonTableRows rows={pageSize} columns={8} />
          </TableBody>
        </Table>
      }
      emptyStateComponent={
        <Tile>
          <EmptyState
            IconComponent={(props) => <Events size={32} {...props} />}
            headerText="No entities available"
            helperText="Currently you don't have any entities."
          />
        </Tile>
      }
    >
      <TableHead>
        <TableRow>
          <TableHeader></TableHeader>
          <TableHeader>Id</TableHeader>
          <TableHeader>Name</TableHeader>
          <TableHeader>Addresses</TableHeader>
          <TableHeader>Phone Numbers</TableHeader>
          <TableHeader>Emails</TableHeader>
          <TableHeader>Entity Type</TableHeader>
          <TableHeader>Inventory</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {searchResults.data?.results.map((el) => {
          return (
            <TableRow key={el.id}>
              <TableCell>
                <div className={style.infoButtons}>
                  {!onEntityClick ? (
                    <DefinitionTooltip
                      className={style.toolTip}
                      align="top"
                      definition="Deduplicate"
                    >
                      <Link to={el.resources.deduplication.href}>
                        <CopyFile size={20} />
                      </Link>
                    </DefinitionTooltip>
                  ) : (
                    onEntityClick(el)
                  )}
                  <EntityDetailsPopover href={el.resources.details.href} />
                </div>
              </TableCell>
              <TableCell>{el.id}</TableCell>
              <TableCell>
                <Link to={el.href}>{el.name}</Link>
              </TableCell>
              <TableCell>
                {el.addresses.map((el) => (
                  <p key={el.streetAddress}>
                    {el.streetAddress ? `${el.streetAddress}, ` : null}
                    {el.city ? `${el.city}, ` : null}
                    {el.state ? `${el.state} ` : null}
                    {el.zipCode ? `${el.zipCode}` : null}
                  </p>
                ))}
              </TableCell>
              <TableCell>
                {el.phoneNumbers.map((el) => (
                  <p key={el.number}>{el.number}</p>
                ))}
              </TableCell>
              <TableCell>
                {el.emailAddresses.map((el) => (
                  <p key={el.email}>{el.email}</p>
                ))}
              </TableCell>
              <TableCell>{el.type?.replace(/(Entity$)/, '')}</TableCell>
              <TableCell>{el.hasInventory ? <Checkmark /> : null}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </PaginatedTable>
  );
};

export { EntitySearch };
